import { Autocomplete, Box, FormHelperText, SelectChangeEvent } from '@mui/material';
import React, { useEffect } from 'react';
import SettingsSelect from 'src/components/select/SettingsSelect';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/store/store';
import { updateFormField } from 'src/features/settings/signatureSlice';
import { TextField } from 'src/components/new/inputs/TextField';
import { Typography } from 'src/components/new/Typography';
import { Spacer } from 'src/components/new/Spacer';
import { TextField as MUITextField } from '@mui/material';
import { addressCountries, ROLES_FOR_SIGNATURE, stateMapper } from 'src/utils/constant';
import { useMask } from '@react-input/mask';

const SignatureForm: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();

  const formData = useSelector((state: RootState) => state.settings.signature.formData);

  const errors = useSelector((state: RootState) => state.settings.signature.formDataErrors);

  const inputRef = useMask({
    mask: '(___) ___-____',
    replacement: { _: /\d/ },
    showMask: true,
  });

  const onCountryChange = (e: SelectChangeEvent) => {
    dispatch(
      updateFormField({
        fieldName: 'country',
        value: e.target.value,
      }),
    );
    dispatch(
      updateFormField({
        fieldName: 'state',
        value: '',
      }),
    );
  };

  return (
    <Box>
      <Box>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <Typography fontSize={16} fontWeight={600} sx={{ marginTop: '-1.5rem' }}>
            Personal Information
          </Typography>
          <Spacer h={16} />
          <div
            style={{
              display: 'flex',
              gap: '1rem',
            }}
          >
            <TextField
              required
              id="fullName"
              label="Full Name"
              name="fullName"
              placeholder="Add..."
              error={errors.fullNameError}
              value={formData.fullName}
              onChange={(e) =>
                dispatch(
                  updateFormField({
                    fieldName: 'fullName',
                    value: e.target.value,
                  }),
                )
              }
              containerSX={{ flex: 1 }}
            />
            <TextField
              id="barNumber"
              label="Bar Number"
              placeholder="Add..."
              name="barNumber"
              error={errors.barNumberError}
              value={formData.barNumber ?? ''}
              onChange={(e) =>
                dispatch(
                  updateFormField({
                    fieldName: 'barNumber',
                    value: e.target.value,
                  }),
                )
              }
              containerSX={{ flex: 1 }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  fontFamily: 'Instrument Sans',
                  lineHeight: '1.25rem',
                  color: '#45464F',
                  mb: 0.5,
                }}
              >
                Role*
              </Typography>
              <Autocomplete
                freeSolo
                options={ROLES_FOR_SIGNATURE}
                forcePopupIcon
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    height: '3.125rem',
                    fontFamily: 'Instrument Sans',
                    lineHeight: '1.25rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: `0.0625rem solid ${errors.clientRoleError ? '#AC3737' : '#7E828B'}`,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: '0.0625rem solid #7E828B',
                    },
                  },
                }}
                onInputChange={(e, newValue) => {
                  dispatch(
                    updateFormField({
                      fieldName: 'clientRole',
                      value: newValue,
                    }),
                  );
                }}
                onChange={(e, newValue) => {
                  dispatch(
                    updateFormField({
                      fieldName: 'clientRole',
                      value: newValue || '',
                    }),
                  );
                }}
                value={formData.clientRole}
                renderInput={(params) => (
                  <MUITextField
                    {...params}
                    inputProps={{ ...params.inputProps, maxLength: 75 }}
                    placeholder="Select..."
                  />
                )}
              />
              <FormHelperText
                sx={{
                  color: '#AC3737',
                  marginTop: 0,
                  fontFamily: 'Instrument Sans',
                  fontSize: '0.875rem',
                  lineHeight: '1rem',
                  height: '1.5rem',
                }}
              >
                {errors.clientRoleError}
              </FormHelperText>
            </Box>
            <TextField
              containerSX={{ flex: 1 }}
              id="firmName"
              label="Firm Name"
              name="firmName"
              placeholder="Add..."
              error={errors.firmNameSignatureError}
              value={formData.firmName ?? ''}
              onChange={(e) =>
                dispatch(
                  updateFormField({
                    fieldName: 'firmName',
                    value: e.target.value,
                  }),
                )
              }
            />
          </div>
          <Typography fontSize={16} fontWeight={600}>
            Address
          </Typography>
          <Spacer h={16} />
          <div style={{ display: 'flex', gap: '1rem' }}>
            <TextField
              required
              id="address1"
              label="Address 1"
              placeholder="Add..."
              name="address1"
              error={errors.address1SignatureError}
              value={formData.addressOne}
              onChange={(e) =>
                dispatch(
                  updateFormField({
                    fieldName: 'addressOne',
                    value: e.target.value,
                  }),
                )
              }
            />
            <TextField
              id="address2"
              label="Address 2"
              name="address2"
              placeholder="Add..."
              error={errors.address2SignatureError}
              helperText={errors.address2SignatureError}
              value={formData.addressTwo ?? ''}
              onChange={(e) =>
                dispatch(
                  updateFormField({
                    fieldName: 'addressTwo',
                    value: e.target.value,
                  }),
                )
              }
            />
          </div>
          <SettingsSelect
            label="Country"
            required
            onChange={onCountryChange}
            data={addressCountries}
            error={errors.countryError}
            value={formData.country || 'United States'}
          />
          <div style={{ display: 'flex', gap: '1rem' }}>
            <TextField
              required
              id="city"
              label="City"
              placeholder="Add..."
              name="city"
              error={errors.citySignatureError}
              value={formData.city}
              onChange={(e) =>
                dispatch(
                  updateFormField({
                    fieldName: 'city',
                    value: e.target.value,
                  }),
                )
              }
              containerSX={{ flex: 2 }}
            />
            {formData.country === 'United States' ? (
              <SettingsSelect
                label="State"
                required
                error={errors.stateSignatureError}
                data={Object.values(stateMapper)}
                onChange={(e) =>
                  dispatch(
                    updateFormField({
                      fieldName: 'state',
                      value: e.target.value,
                    }),
                  )
                }
                autoComplete="address-level1"
                value={formData.state}
                containerSX={{ flex: 1 }}
              />
            ) : (
              <TextField
                label="State"
                id="state"
                name="state"
                placeholder="Add..."
                error={errors.stateSignatureError}
                onChange={(e) =>
                  dispatch(
                    updateFormField({
                      fieldName: 'state',
                      value: e.target.value,
                    }),
                  )
                }
                required
                value={formData.state}
                containerSX={{ flex: 1 }}
              />
            )}
            <TextField
              required
              id="zip"
              label={formData.country === 'Canada' ? 'Postal code' : 'ZIP'}
              name="zip"
              placeholder="Add..."
              error={errors.zipSignatureError}
              value={formData.zipCode}
              onChange={(e) =>
                dispatch(
                  updateFormField({
                    fieldName: 'zipCode',
                    value: e.target.value,
                  }),
                )
              }
              containerSX={{ flex: 1 }}
            />
          </div>
        </Box>
      </Box>

      <Box>
        <Typography fontSize={16} fontWeight={600}>
          Contact Details
        </Typography>
        <Spacer h={16} />
        <div style={{ display: 'flex', gap: '1rem' }}>
          <TextField
            required
            id="phone"
            label="Phone"
            placeholder="(___) ___-____"
            inputRef={inputRef}
            name="phone"
            error={errors.phoneError}
            value={formData.phone}
            onChange={(e) =>
              dispatch(
                updateFormField({
                  fieldName: 'phone',
                  value: e.target.value,
                }),
              )
            }
          />
          <TextField
            id="email"
            label="Email"
            name="email"
            error={errors.emailError}
            placeholder="Add..."
            helperText={errors.emailError}
            value={formData.email ?? ''}
            onChange={(e) =>
              dispatch(
                updateFormField({
                  fieldName: 'email',
                  value: e.target.value,
                }),
              )
            }
          />
          <TextField
            id="fax"
            label="Fax"
            placeholder="Add..."
            name="fax"
            error={errors.faxError}
            helperText={errors.faxError}
            value={formData.fax ?? ''}
            onChange={(e) => dispatch(updateFormField({ fieldName: 'fax', value: e.target.value }))}
          />
        </div>
      </Box>
    </Box>
  );
};

export default SignatureForm;
